import { Transformer } from "@/src/utils/transformers"
import {
  BinaryExpression,
  CallExpression,
  Node,
  PropertyAccessExpression,
  PropertyAssignment,
  SourceFile,
  SyntaxKind,
} from "ts-morph"
import { Framework, FRAMEWORKS } from "@/src/utils/frameworks"
import { getProjectInfo } from "@/src/utils/get-project-info"
import { Config } from "@/src/utils/get-config"

/**
 * Defines a rule for excluding components based on name matching patterns
 */
export interface ComponentExclusionRule {
  name: string // The name or pattern to match against
  matchType: "exact" | "contains" | "startsWith" | "endsWith" | "regex" // How to match the name
  caseSensitive?: boolean // Whether matching should be case sensitive
  pattern?: string // Regex pattern (used when matchType is "regex")
  onlyInFiles?: string[]
}

interface NodeToRemove {
  node: any
  newText?: string
  action: "remove" | "replace"
}

/**
 * Default set of exclusion rules for common unwanted component patterns
 * These target development, testing, and internal components that shouldn't
 * be included in production builds or public APIs
 */
/**
 * Default set of exclusion rules for common unwanted component patterns
 * These target development, testing, and internal components that shouldn't
 * be included in production builds or public APIs
 */
const DEFAULT_EXCLUSIONS: ComponentExclusionRule[] = [
  {
    name: "cta",
    matchType: "contains",
    caseSensitive: false,
    onlyInFiles: ["notion-like-editor.tsx"],
  },
  {
    name: "initialContent",
    matchType: "exact",
    caseSensitive: true,
    onlyInFiles: ["notion-like-editor.tsx"],
  },
  {
    name: "JSONContent",
    matchType: "exact",
    caseSensitive: true,
    onlyInFiles: ["notion-like-editor.tsx"],
  },
  {
    name: "onCreate",
    matchType: "exact",
    caseSensitive: true,
    onlyInFiles: ["notion-like-editor.tsx"],
  },
  {
    name: "getDocumentId",
    matchType: "exact",
    caseSensitive: true,
    onlyInFiles: ["notion-like-editor.tsx"],
  },
]

/**
 * Determines whether a component should be excluded based on the provided rules
 * @param componentName - The name of the component to check
 * @param rules - Array of exclusion rules to apply
 * @returns true if the component should be excluded, false otherwise
 */
function shouldExcludeComponent(
  componentName: string,
  rules: ComponentExclusionRule[],
  filePath: string
): boolean {
  return rules.some((rule) => {
    // Check file path restriction
    if (
      rule.onlyInFiles &&
      !rule.onlyInFiles.some((f) => filePath.endsWith(f))
    ) {
      return false
    }

    const name = rule.caseSensitive
      ? componentName
      : componentName.toLowerCase()
    const ruleName = rule.caseSensitive ? rule.name : rule.name.toLowerCase()

    switch (rule.matchType) {
      case "exact":
        return name === ruleName
      case "contains":
        return name.includes(ruleName)
      case "startsWith":
        return name.startsWith(ruleName)
      case "endsWith":
        return name.endsWith(ruleName)
      case "regex":
        if (!rule.pattern) return false
        const regex = new RegExp(rule.pattern, rule.caseSensitive ? "" : "i")
        return regex.test(name)
      default:
        return false
    }
  })
}

function getFrameworkEnvVarName(
  constantName: string,
  framework: Framework
): string {
  const frameworkName = framework.name

  if (
    frameworkName === FRAMEWORKS["next-app"].name ||
    frameworkName === FRAMEWORKS["next-pages"].name
  ) {
    return `NEXT_PUBLIC_${constantName}`
  } else if (frameworkName === FRAMEWORKS.astro.name) {
    return `PUBLIC_${constantName}`
  } else if (
    frameworkName === FRAMEWORKS.vite.name ||
    frameworkName === FRAMEWORKS["tanstack-start"].name ||
    frameworkName === FRAMEWORKS["react-router"].name
  ) {
    return `VITE_${constantName}`
  } else if (
    frameworkName === FRAMEWORKS.laravel.name ||
    frameworkName === FRAMEWORKS.manual.name
  ) {
    return constantName
  } else {
    return constantName
  }
}

function stripFrameworkPrefix(varName: string): string {
  return varName.replace(/^(NEXT_PUBLIC_|VITE_|PUBLIC_)/, "")
}

async function transformEnvironmentVariables(
  sourceFile: SourceFile,
  config: Config
): Promise<void> {
  const projectInfo = await getProjectInfo(config.resolvedPaths.cwd)

  if (!projectInfo) {
    return
  }

  const nodesToTransform: Array<{
    node: BinaryExpression | PropertyAccessExpression | CallExpression
    newText: string
  }> = []

  // 1. Transform environment variable access
  const binaryExpressions = sourceFile.getDescendantsOfKind(
    SyntaxKind.BinaryExpression
  )

  for (const binaryExpr of binaryExpressions) {
    if (binaryExpr.getOperatorToken().getKind() === SyntaxKind.BarBarToken) {
      const left = binaryExpr.getLeft()
      const leftText = left.getText()

      if (leftText.match(/^process\.env\.[A-Z_]+$/)) {
        const match = leftText.match(/process\.env\.([A-Z_]+)/)
        if (match && match[1]) {
          const constantName = stripFrameworkPrefix(match[1])
          const frameworkName = projectInfo.framework.name

          let transformedEnvVar: string
          if (frameworkName === FRAMEWORKS.astro.name) {
            transformedEnvVar = `import.meta.env.PUBLIC_${constantName}`
          } else if (
            frameworkName === FRAMEWORKS.vite.name ||
            frameworkName === FRAMEWORKS["tanstack-start"].name ||
            frameworkName === FRAMEWORKS["react-router"].name
          ) {
            transformedEnvVar = `import.meta.env.VITE_${constantName}`
          } else if (
            frameworkName === FRAMEWORKS["next-app"].name ||
            frameworkName === FRAMEWORKS["next-pages"].name
          ) {
            transformedEnvVar = `process.env.NEXT_PUBLIC_${constantName}`
          } else {
            transformedEnvVar = `process.env.${constantName}`
          }

          const rightSide = binaryExpr.getRight().getText()
          const newExpression = `${transformedEnvVar} || ${rightSide}`

          nodesToTransform.push({
            node: binaryExpr,
            newText: newExpression,
          })
        }
      }
    }
  }

  // 2. Transform alert calls that contain environment variable lists
  const callExpressions = sourceFile.getDescendantsOfKind(
    SyntaxKind.CallExpression
  )

  for (const callExpr of callExpressions) {
    const expression = callExpr.getExpression()
    const expressionText = expression.getText()

    // Check for alert calls
    if (expressionText.match(/^alert$/)) {
      const args = callExpr.getArguments()
      if (args.length > 0) {
        const firstArg = args[0]
        const argText = firstArg.getText()

        // Look for template literals or strings that contain environment variable lists
        if (
          argText.includes("TIPTAP_COLLAB_TOKEN") ||
          argText.includes("TIPTAP_COLLAB_DOC_PREFIX") ||
          argText.includes("TIPTAP_AI_TOKEN")
        ) {
          let newArgText = argText

          const envVarsToTransform = [
            "TIPTAP_COLLAB_DOC_PREFIX",
            "TIPTAP_COLLAB_APP_ID",
            "TIPTAP_COLLAB_TOKEN",
            "TIPTAP_AI_APP_ID",
            "TIPTAP_AI_TOKEN",
            "USE_JWT_TOKEN_API_ENDPOINT",
          ]

          for (let constantName of envVarsToTransform) {
            const frameworkEnvVarName = getFrameworkEnvVarName(
              constantName,
              projectInfo.framework
            )
            newArgText = newArgText.replace(
              new RegExp(constantName, "g"),
              frameworkEnvVarName
            )
          }

          if (newArgText !== argText) {
            const newCallExpr = `${expressionText}(${newArgText})`
            nodesToTransform.push({
              node: callExpr,
              newText: newCallExpr,
            })
          }
        }
      }
    }
  }

  // 3. Transform standalone process.env
  const propertyAccessExpressions = sourceFile.getDescendantsOfKind(
    SyntaxKind.PropertyAccessExpression
  )

  for (const propAccess of propertyAccessExpressions) {
    const propAccessText = propAccess.getText()

    if (propAccessText.match(/^process\.env\.[A-Z_]+$/)) {
      const parent = propAccess.getParent()
      if (parent?.getKind() === SyntaxKind.BinaryExpression) {
        continue
      }

      const match = propAccessText.match(/process\.env\.([A-Z_]+)/)
      if (match && match[1]) {
        const constantName = stripFrameworkPrefix(match[1])
        const frameworkName = projectInfo.framework.name

        let transformedEnvVar: string
        if (frameworkName === FRAMEWORKS.astro.name) {
          transformedEnvVar = `import.meta.env.PUBLIC_${constantName}`
        } else if (
          frameworkName === FRAMEWORKS.vite.name ||
          frameworkName === FRAMEWORKS["tanstack-start"].name ||
          frameworkName === FRAMEWORKS["react-router"].name
        ) {
          transformedEnvVar = `import.meta.env.VITE_${constantName}`
        } else if (
          frameworkName === FRAMEWORKS["next-app"].name ||
          frameworkName === FRAMEWORKS["next-pages"].name
        ) {
          transformedEnvVar = `process.env.NEXT_PUBLIC_${constantName}`
        } else {
          transformedEnvVar = `process.env.${constantName}`
        }

        nodesToTransform.push({
          node: propAccess,
          newText: transformedEnvVar,
        })
      }
    }
  }

  // Apply all transformations
  for (const { node, newText } of nodesToTransform) {
    try {
      node.replaceWithText(newText)
    } catch (error) {
      console.warn(`Skipping transformation of node: ${error}`)
    }
  }
}

/**
 * Main transformer function that removes excluded components from TypeScript/React source files
 * This transformer processes various AST nodes to remove unwanted components based on exclusion rules
 */
export const transformRemoveComponents: Transformer = async ({
  sourceFile,
  config,
}) => {
  // Use default exclusion rules (could be made configurable in the future)
  const exclusionRules: ComponentExclusionRule[] = DEFAULT_EXCLUSIONS
  const filePath = sourceFile.getFilePath()
  // Collect all nodes to be modified instead of modifying immediately
  const nodesToModify: NodeToRemove[] = []

  // STEP 1: Collect excluded component imports
  const importDeclarations = sourceFile.getImportDeclarations()

  for (const importDeclaration of importDeclarations) {
    const moduleSpecifier = importDeclaration.getModuleSpecifierValue()

    // Check if the entire import module should be removed
    if (
      exclusionRules.some((rule) =>
        shouldExcludeComponent(moduleSpecifier, [rule], filePath)
      )
    ) {
      nodesToModify.push({ node: importDeclaration, action: "remove" })
      continue
    }

    // Handle default imports (import defaultName from "...")
    const defaultImport = importDeclaration.getDefaultImport()
    if (
      defaultImport &&
      shouldExcludeComponent(defaultImport.getText(), exclusionRules, filePath)
    ) {
      nodesToModify.push({ node: importDeclaration, action: "remove" })
      continue
    }

    // Remove specific named imports that match exclusion rules
    const namedImports = importDeclaration.getNamedImports()
    const excludedImports = namedImports.filter((namedImport) =>
      shouldExcludeComponent(namedImport.getName(), exclusionRules, filePath)
    )

    if (excludedImports.length > 0) {
      excludedImports.forEach((excludedImport) =>
        nodesToModify.push({ node: excludedImport, action: "remove" })
      )

      // Check if this would remove all named imports
      const remainingImports = namedImports.filter(
        (namedImport) => !excludedImports.includes(namedImport)
      )

      if (remainingImports.length === 0 && !defaultImport) {
        nodesToModify.push({ node: importDeclaration, action: "remove" })
      }
    }
  }

  // STEP 2: Collect excluded component exports
  const exportDeclarations = sourceFile.getExportDeclarations()

  for (const exportDeclaration of exportDeclarations) {
    const namedExports = exportDeclaration.getNamedExports()
    const excludedExports = namedExports.filter((namedExport) =>
      shouldExcludeComponent(namedExport.getName(), exclusionRules, filePath)
    )

    if (excludedExports.length > 0) {
      excludedExports.forEach((excludedExport) =>
        nodesToModify.push({ node: excludedExport, action: "remove" })
      )

      // Check if this would remove all named exports
      const remainingExports = namedExports.filter(
        (namedExport) => !excludedExports.includes(namedExport)
      )

      if (remainingExports.length === 0) {
        nodesToModify.push({ node: exportDeclaration, action: "remove" })
      }
    }
  }

  // STEP 3: Collect excluded component variable declarations
  const variableStatements = sourceFile.getVariableStatements()

  for (const variableStatement of variableStatements) {
    const declarations = variableStatement.getDeclarations()
    const excludedDeclarations = declarations.filter((declaration) =>
      shouldExcludeComponent(declaration.getName(), exclusionRules, filePath)
    )

    if (excludedDeclarations.length > 0) {
      if (declarations.length === excludedDeclarations.length) {
        // Remove entire variable statement if all declarations are excluded
        nodesToModify.push({ node: variableStatement, action: "remove" })
      } else {
        // Remove only excluded declarations, keep others
        excludedDeclarations.forEach((declaration) =>
          nodesToModify.push({ node: declaration, action: "remove" })
        )
      }
    }
  }

  // STEP 4: Collect excluded component function declarations
  const functionDeclarations = sourceFile.getFunctions()

  for (const functionDeclaration of functionDeclarations) {
    const functionName = functionDeclaration.getName()
    if (
      functionName &&
      shouldExcludeComponent(functionName, exclusionRules, filePath)
    ) {
      nodesToModify.push({ node: functionDeclaration, action: "remove" })
    }
  }

  // STEP 5: Collect excluded component interface declarations
  const interfaceDeclarations = sourceFile.getInterfaces()

  for (const interfaceDeclaration of interfaceDeclarations) {
    const interfaceName = interfaceDeclaration.getName()
    if (shouldExcludeComponent(interfaceName, exclusionRules, filePath)) {
      nodesToModify.push({ node: interfaceDeclaration, action: "remove" })
    }
  }

  // STEP 6: Collect excluded component type aliases
  const typeAliases = sourceFile.getTypeAliases()

  for (const typeAlias of typeAliases) {
    const typeName = typeAlias.getName()
    if (shouldExcludeComponent(typeName, exclusionRules, filePath)) {
      nodesToModify.push({ node: typeAlias, action: "remove" })
    }
  }

  // STEP 6.5: Collect excluded interface property signatures
  const propertySignatures = sourceFile.getDescendantsOfKind(
    SyntaxKind.PropertySignature
  )

  for (const propertySignature of propertySignatures) {
    const propName = propertySignature.getName()
    if (
      propName &&
      shouldExcludeComponent(propName, exclusionRules, filePath)
    ) {
      nodesToModify.push({ node: propertySignature, action: "remove" })
    }
  }

  // STEP 7: Collect excluded JSX elements from React components
  const jsxElements = sourceFile.getDescendantsOfKind(SyntaxKind.JsxElement)

  for (const jsxElement of jsxElements) {
    const tagName = jsxElement.getOpeningElement().getTagNameNode().getText()
    if (shouldExcludeComponent(tagName, exclusionRules, filePath)) {
      nodesToModify.push({
        node: jsxElement,
        action: "replace",
        newText: "",
      })
    }
  }

  // STEP 8: Collect excluded self-closing JSX elements
  const jsxSelfClosingElements = sourceFile.getDescendantsOfKind(
    SyntaxKind.JsxSelfClosingElement
  )

  for (const jsxSelfClosingElement of jsxSelfClosingElements) {
    const tagName = jsxSelfClosingElement.getTagNameNode().getText()
    if (shouldExcludeComponent(tagName, exclusionRules, filePath)) {
      nodesToModify.push({
        node: jsxSelfClosingElement,
        action: "replace",
        newText: "",
      })
    }
  }

  // STEP 9: Collect excluded component properties from objects
  const propertyAssignments = sourceFile.getDescendantsOfKind(
    SyntaxKind.PropertyAssignment
  )

  for (const propertyAssignment of propertyAssignments) {
    const propertyName = propertyAssignment.getName()
    if (
      propertyName &&
      shouldExcludeComponent(propertyName, exclusionRules, filePath)
    ) {
      nodesToModify.push({ node: propertyAssignment, action: "remove" })
    }
  }

  // STEP 10: Collect excluded JSX attributes
  const jsxAttributes = sourceFile.getDescendantsOfKind(SyntaxKind.JsxAttribute)

  for (const jsxAttribute of jsxAttributes) {
    const attributeName = jsxAttribute.getNameNode().getText()
    if (
      attributeName &&
      shouldExcludeComponent(attributeName, exclusionRules, filePath)
    ) {
      nodesToModify.push({ node: jsxAttribute, action: "remove" })
    }
  }

  // STEP 11: Collect excluded parameters from function/method signatures
  const parameters = sourceFile.getDescendantsOfKind(SyntaxKind.Parameter)

  for (const parameter of parameters) {
    const parameterName = parameter.getName()
    if (
      parameterName &&
      shouldExcludeComponent(parameterName, exclusionRules, filePath)
    ) {
      const paramList = parameter.getParent()

      // Only proceed if the parent is a function-like structure
      if (
        paramList &&
        (Node.isFunctionDeclaration(paramList) ||
          Node.isArrowFunction(paramList) ||
          Node.isFunctionExpression(paramList) ||
          Node.isMethodDeclaration(paramList))
      ) {
        const allParams = paramList.getParameters()
        const index = allParams.indexOf(parameter)

        // Handle comma removal for cleaner output
        if (index !== -1) {
          if (index === allParams.length - 1 && index > 0) {
            const prev = parameter.getPreviousSibling()
            if (prev?.getKind() === SyntaxKind.CommaToken) {
              nodesToModify.push({ node: prev, action: "remove" })
            }
          } else {
            const next = parameter.getNextSibling()
            if (next?.getKind() === SyntaxKind.CommaToken) {
              nodesToModify.push({ node: next, action: "remove" })
            }
          }

          nodesToModify.push({ node: parameter, action: "remove" })
        }
      }
    }
  }

  // STEP 11.1: Handle function arguments in call expressions
  const callExpressions = sourceFile.getDescendantsOfKind(
    SyntaxKind.CallExpression
  )

  for (const callExpr of callExpressions) {
    const args = callExpr.getArguments()
    for (let i = 0; i < args.length; i++) {
      const arg = args[i]
      const argText = arg.getText()

      if (shouldExcludeComponent(argText, exclusionRules, filePath)) {
        // Handle comma removal for cleaner output
        if (i === args.length - 1) {
          // Last argument - remove preceding comma if exists
          const prevSibling = arg.getPreviousSibling()
          if (prevSibling && prevSibling.getKind() === SyntaxKind.CommaToken) {
            nodesToModify.push({ node: prevSibling, action: "remove" })
          }
        } else {
          // Not last argument - remove following comma if exists
          const nextSibling = arg.getNextSibling()
          if (nextSibling && nextSibling.getKind() === SyntaxKind.CommaToken) {
            nodesToModify.push({ node: nextSibling, action: "remove" })
          }
        }

        nodesToModify.push({ node: arg, action: "remove" })
      }
    }
  }

  // STEP 11.5: Collect excluded binding elements (destructured parameters)
  const bindingElements = sourceFile.getDescendantsOfKind(
    SyntaxKind.BindingElement
  )

  for (const bindingElement of bindingElements) {
    const bindingName = bindingElement.getName()
    if (
      bindingName &&
      shouldExcludeComponent(bindingName, exclusionRules, filePath)
    ) {
      // Handle comma cleanup (already correct in your code)
      const bindingPattern = bindingElement.getParent()
      if (bindingPattern) {
        const allBindings = bindingPattern.getDescendantsOfKind(
          SyntaxKind.BindingElement
        )
        const bindingIndex = allBindings.indexOf(bindingElement)

        if (bindingIndex === allBindings.length - 1) {
          const prevSibling = bindingElement.getPreviousSibling()
          if (prevSibling?.getKind() === SyntaxKind.CommaToken) {
            nodesToModify.push({ node: prevSibling, action: "remove" })
          }
        } else {
          const nextSibling = bindingElement.getNextSibling()
          if (nextSibling?.getKind() === SyntaxKind.CommaToken) {
            nodesToModify.push({ node: nextSibling, action: "remove" })
          }
        }
      }

      nodesToModify.push({ node: bindingElement, action: "remove" })
    }
  }

  // STEP 12: Collect excluded identifiers in function calls and expressions
  const identifiers = sourceFile.getDescendantsOfKind(SyntaxKind.Identifier)

  for (const identifier of identifiers) {
    const identifierName = identifier.getText()
    if (shouldExcludeComponent(identifierName, exclusionRules, filePath)) {
      const parent = identifier.getParent()

      // Handle different contexts where the identifier appears
      if (parent) {
        const parentKind = parent.getKind()

        // Remove entire call expression if it's calling an excluded function
        if (parentKind === SyntaxKind.CallExpression) {
          const callExpr = parent as CallExpression
          if (callExpr.getExpression() === identifier) {
            // This is a function call like getDocumentId()
            nodesToModify.push({ node: callExpr, action: "remove" })
          } else {
            // This is an argument in a function call
            const args = callExpr.getArguments()
            const argIndex = args.findIndex((arg) => arg === identifier)
            if (argIndex !== -1) {
              nodesToModify.push({ node: identifier, action: "remove" })
            }
          }
        }

        // Remove property access expressions with excluded identifiers
        if (parentKind === SyntaxKind.PropertyAccessExpression) {
          const propAccess = parent as PropertyAccessExpression
          if (propAccess.getName() === identifierName) {
            nodesToModify.push({ node: propAccess, action: "remove" })
          }
        }

        // Remove variable references and expressions
        if (
          parentKind === SyntaxKind.VariableDeclaration ||
          parentKind === SyntaxKind.BinaryExpression ||
          parentKind === SyntaxKind.IfStatement ||
          parentKind === SyntaxKind.ConditionalExpression
        ) {
          nodesToModify.push({ node: identifier, action: "remove" })
        }

        // Remove from object literal property values
        if (parentKind === SyntaxKind.PropertyAssignment) {
          const propAssignment = parent as PropertyAssignment
          if (propAssignment.getInitializer() === identifier) {
            nodesToModify.push({ node: propAssignment, action: "remove" })
          }
        }
      }
    }
  }

  // STEP 13: Collect excluded shorthand property assignments (e.g., { initialContent })
  const shorthandPropertyAssignments = sourceFile.getDescendantsOfKind(
    SyntaxKind.ShorthandPropertyAssignment
  )

  for (const shorthandProp of shorthandPropertyAssignments) {
    const propName = shorthandProp.getName()
    if (
      propName &&
      shouldExcludeComponent(propName, exclusionRules, filePath)
    ) {
      nodesToModify.push({ node: shorthandProp, action: "remove" })
    }
  }

  // Apply environment variable transformations first (before removing nodes)
  await transformEnvironmentVariables(sourceFile, config)

  // STEP 16: Apply all collected modifications
  // Sort by position (deepest nodes first) to avoid accessing removed parents
  nodesToModify.sort((a, b) => {
    const aStart = a.node.getStart?.() ?? 0
    const bStart = b.node.getStart?.() ?? 0
    return bStart - aStart // Reverse order - process deepest/rightmost first
  })

  // Apply transformations safely
  for (const { node, action, newText } of nodesToModify) {
    try {
      // Check if node still exists and hasn't been removed
      if (node.wasForgotten?.() === true) {
        continue // Skip nodes that were already removed
      }

      if (action === "remove") {
        if (typeof node.remove === "function") {
          node.remove()
        }
      } else if (action === "replace" && newText !== undefined) {
        node.replaceWithText(newText)
      }
    } catch (error) {
      // console.warn(`Skipping transformation of node: ${error}`)
      // Continue with other transformations even if one fails
    }
  }

  // Return the modified source file with excluded components removed
  return sourceFile
}
